import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

type RequestEvent = CustomEvent<{
  url: URL;
  fetchOptions: { method: string; headers: Record<string, string> };
}>;
type ResponseEvent = CustomEvent<{
  fetchResponse: {
    succeeded: boolean;
    redirected: boolean;
    isHTML: boolean;
    location: URL;
    statusCode: number;
    responseHTML: Promise<string | undefined>;
  };
}>;

// Shared drawer adapter; only migrated endpoints opt into full-page redirect responses.
export default class extends Controller<HTMLElement> {
  private submissions = new WeakSet<HTMLFormElement>();

  prepare(event: RequestEvent): void {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    this.submissions.delete(form);

    const { url, fetchOptions } = event.detail;
    const { headers, method } = fetchOptions;
    if (form.closest("turbo-frame") !== this.element || headers["Turbo-Frame"] !== this.element.id) return;
    if (["get", "head"].includes(method.toLowerCase()) || !this.isMigratedAction(url)) return;
    // A form opened mid-task (the outreach visit sheet's "Add a neighbor") wants the
    // ordinary frame submission: its response is a stream for another frame, not a page.
    if (form.dataset.frameRedirect === "off") return;

    this.submissions.add(form);
    // The followed redirect must contain a full document for the page-level visit.
    delete headers["Turbo-Frame"];
    headers.Accept = "text/html, application/xhtml+xml";
  }

  async redirect(event: ResponseEvent): Promise<void> {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !this.submissions.delete(form)) return;

    const response = event.detail.fetchResponse;
    if (!response.succeeded || !response.redirected || !response.isHTML) return;

    event.preventDefault();
    const responseHTML = await response.responseHTML;
    if (responseHTML === undefined || !form.isConnected || !this.element.contains(form)) return;

    // Let the existing modal animate out before Turbo replaces or morphs it away.
    const closing = this.dispatch("before-visit", { detail: { finished: Promise.resolve() } });
    await closing.detail.finished;
    if (!form.isConnected || !this.element.contains(form)) return;

    Turbo.cache.clear();
    Turbo.visit(response.location.href, {
      action: response.location.href === window.location.href ? "replace" : "advance",
      shouldCacheSnapshot: false,
      response: { statusCode: response.statusCode, responseHTML, redirected: true },
    });
  }

  private isMigratedAction(url: URL): boolean {
    if (url.origin !== window.location.origin) return false;

    // Keep this allowlist in sync with the neighbor profile redirect trial.
    return url.pathname === "/neighbors" ||
      /^\/neighbors\/\d+\/female_specific$/.test(url.pathname) ||
      /^\/neighbors\/\d+\/update_(basic_info|contact_info)$/.test(url.pathname) ||
      /^\/neighbors\/\d+\/update_(network_)?custom_fields\/\d+$/.test(url.pathname) ||
      /^\/neighbors\/\d+\/(addresses|contacts)(\/\d+)?$/.test(url.pathname);
  }
}
